import { Controller } from "@hotwired/stimulus"

// Polling fallback for package status updates
// Ensures updates work even when WebSocket connection fails
export default class extends Controller {
  static values = {
    packageId: String,
    status: String,
    refreshUrl: String
  }

  static targets = []

  connect() {
    this.pollingInterval = null
    this.pollDelay = 3000 // 3 second polling when needed (was 5000)
    this.maxPollDelay = 10000 // Max 10 seconds between polls (was 15000)
    this.errorCount = 0
    this.maxErrors = 5
    this.pollCount = 0
    this.lastUpdateTime = Date.now()
    this.watchdogTimer = null

    // Listen for visibility changes to pause/resume polling
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.handleVisibilityChange)

    // POLLING STRATEGY: Only poll as a FALLBACK if WebSockets fail
    // 1. Wait 5 seconds to give WebSockets a chance to deliver updates (was 15s)
    // 2. If still processing after 5 seconds with no update, START polling
    // 3. This way WebSockets are primary, polling is fallback only
    if (this.hasStatusValue && this.isProcessing()) {
      console.log(`[PackageStatus] Controller connected for package ${this.packageIdValue}, status: ${this.statusValue}`)
      console.log(`[PackageStatus] Waiting 5s for WebSocket updates before starting fallback polling...`)

      this.watchdogTimer = setTimeout(() => {
        // After 5 seconds, if still processing, assume WebSocket isn't working
        if (this.isProcessing()) {
          console.log(`[PackageStatus] No WebSocket update received, starting fallback polling`)
          this.startPolling()
        }
      }, 5000) // Changed from 15000
    } else {
      console.log(`[PackageStatus] Controller connected but not polling (status: ${this.statusValue || 'unknown'})`)
    }
  }

  disconnect() {
    this.stopPolling()
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer)
      this.watchdogTimer = null
    }
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
  }

  isProcessing() {
    const processingStates = ["pending", "visible_watermarking", "invisible_watermarking", "archiving"]
    return processingStates.includes(this.statusValue)
  }

  startPolling() {
    // Don't start if already polling
    if (this.pollingInterval) return

    // Don't poll if tab is hidden
    if (document.hidden) return

    console.log(`[PackageStatus] Starting polling for package ${this.packageIdValue} (status: ${this.statusValue})`)

    this.pollingInterval = setInterval(() => {
      this.poll()
    }, this.pollDelay)
  }

  stopPolling() {
    if (this.pollingInterval) {
      console.log(`[PackageStatus] Stopping polling for package ${this.packageIdValue}`)
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async poll() {
    try {
      const response = await fetch(this.refreshUrlValue, {
        headers: {
          "Accept": "text/html"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      // Get the updated HTML
      const html = await response.text()

      // Replace the frame content
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")
      const newContent = doc.querySelector("#package_status")

      if (newContent) {
        this.element.innerHTML = newContent.innerHTML

        const newStatusValue = newContent.dataset.packageStatusStatusValue
        if (newStatusValue) {
          this.statusValue = newStatusValue
        }

        const newRefreshUrl = newContent.dataset.packageStatusRefreshUrlValue
        if (newRefreshUrl) {
          this.refreshUrlValue = newRefreshUrl
        }
      }

      // Reset error count on success
      this.errorCount = 0
      this.pollDelay = 3000 // Reset to default

      // Check if we should stop polling (package completed or errored)
      // Parse the updated status from the response
      const newStatus = this.extractStatusFromHtml(html)
      if (newStatus && !this.isProcessingStatus(newStatus)) {
        console.log(`[PackageStatus] Package ${this.packageIdValue} reached terminal state: ${newStatus}`)
        this.stopPolling()
      }

    } catch (error) {
      console.error(`[PackageStatus] Polling error:`, error)
      this.errorCount++

      if (this.errorCount >= this.maxErrors) {
        console.error(`[PackageStatus] Too many errors, stopping polling`)
        this.stopPolling()
      } else {
        // Exponential backoff on errors (up to maxPollDelay)
        this.pollDelay = Math.min(this.pollDelay * 1.5, this.maxPollDelay)
        console.log(`[PackageStatus] Backing off to ${this.pollDelay}ms`)
      }
    }
  }

  extractStatusFromHtml(html) {
    // Parse HTML and look for data-status attribute
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")

    // Find element with data-status attribute
    const statusElement = doc.querySelector("[data-status]")
    if (statusElement) {
      return statusElement.dataset.status
    }

    // Fallback to string matching for backwards compatibility
    if (html.includes('alert-success') && html.includes('Your download is ready')) {
      return 'ready'
    } else if (html.includes('alert-error') || html.includes('Package generation failed')) {
      return 'errored'
    } else if (html.includes('alert-warning') && html.includes('no longer available')) {
      return 'revoked'
    } else if (html.includes('Preparing Your Download')) {
      return 'processing'
    }
    return null
  }

  isProcessingStatus(status) {
    const processingStates = ["pending", "visible_watermarking", "invisible_watermarking", "archiving", "processing"]
    return processingStates.includes(status)
  }

  handleVisibilityChange() {
    if (document.hidden) {
      // Tab hidden - stop polling to save resources
      this.stopPolling()
    } else {
      // Tab visible - resume polling if needed
      if (this.isProcessing()) {
        this.startPolling()
      }
    }
  }

  // Allow manual refresh trigger (can be called from other controllers)
  refresh() {
    this.poll()
  }

  // Update status value when it changes (called by Turbo Streams potentially)
  statusValueChanged(newStatus, oldStatus) {
    if (newStatus !== oldStatus) {
      console.log(`[PackageStatus] Status changed: ${oldStatus} → ${newStatus}`)

      // Status changed = WebSocket is working! Cancel watchdog timer
      if (this.watchdogTimer) {
        console.log(`[PackageStatus] WebSocket update received, canceling fallback polling`)
        clearTimeout(this.watchdogTimer)
        this.watchdogTimer = null
      }

      // Also stop polling if it started
      if (this.pollingInterval) {
        console.log(`[PackageStatus] WebSocket working, stopping redundant polling`)
        this.stopPolling()
      }

      // Update last update time
      this.lastUpdateTime = Date.now()

      // If status changed to processing, restart watchdog
      if (this.isProcessingStatus(newStatus) && !this.pollingInterval) {
        this.watchdogTimer = setTimeout(() => {
          if (this.isProcessing()) {
            console.log(`[PackageStatus] No update for 5s, starting fallback polling`)
            this.startPolling()
          }
        }, 5000) // Changed from 15000
      } else if (!this.isProcessingStatus(newStatus)) {
        // Terminal state reached, ensure everything is stopped
        this.stopPolling()
        if (this.watchdogTimer) {
          clearTimeout(this.watchdogTimer)
          this.watchdogTimer = null
        }
      }
    }
  }
}
